<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { object, ref as yupRef } from "yup";
import UsersService from "@/api/models/auth/users";
import { useSnackStore } from "@/stores/snackStore";
import { useRoute, useRouter } from "vue-router";
import { computed, ref } from "vue";
import PasswordSafetyIndicator from "@/components/shared/PasswordSafetyIndicator.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import { createRequiredField } from "@/utils/validationHelpers";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

definePage({
  meta: {
    title: "models.auth.resetPasswordConfirm.title",
    layout: "login",
    requiresAuth: false,
  },
});

const route = useRoute();
const routeParams = route.params as { uid: string; token: string };

const { t } = useI18n();
const snackStore = useSnackStore();
const router = useRouter();

const validationSchema = object({
  new_password: createRequiredField(t, "generic.fields.newPassword"),
  new_password_confirm: createRequiredField(t, "models.auth.invitation.confirmPassword").oneOf(
    [yupRef("new_password")],
    t("models.auth.resetPasswordConfirm.passwordsMatch")
  ),
});

const { handleSubmit, values } = useForm<{
  new_password: string;
  new_password_confirm: string;
}>({
  validationSchema,
});

const newPassword = computed(() => values.new_password ?? "");
const isPasswordValid = ref(false);

const { mutateAsync: performResetPasswordConfirm, isPending } = useGenericMutation<void, string>({
  mutationFn: async (newPassword) => {
    return await UsersService.resetPasswordConfirm(newPassword, routeParams.uid, routeParams.token);
  },
  options: {
    onSuccess: async (): Promise<void> => {
      snackStore.showSuccess(t("models.auth.resetPasswordConfirm.success"));
      await router.push("/auth/login");
    },
  },
});

const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const submit = handleSubmit((values) => {
  void runSubmitFlow(async () => {
    await performResetPasswordConfirm(values.new_password);
  });
});
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
</script>

<template>
  <div class="tw:!ml-auto tw:w-full tw:md:w-1/2 tw:h-full tw:flex tw:items-center">
    <v-form
      class="tw:w-full tw:md:w-2/3"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
    >
      <div class="tw:font-bold tw:text-2xl tw:md:text-4xl tw:!mb-1">
        {{ t("models.auth.resetPasswordConfirm.title") }}
      </div>
      <div class="tw:!mt-10">
        <text-field
          name="new_password"
          :label="t('generic.fields.newPassword')"
          :type="showNewPassword ? 'text' : 'password'"
          :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showNewPassword = !showNewPassword"
        />
        <text-field
          name="new_password_confirm"
          :label="t('models.auth.invitation.confirmPassword')"
          :type="showConfirmPassword ? 'text' : 'password'"
          :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showConfirmPassword = !showConfirmPassword"
        />

        <password-safety-indicator
          v-model:is-valid="isPasswordValid"
          :password="newPassword"
          class="tw:!my-3"
        />
      </div>
      <div class="tw:flex tw:justify-between tw:items-center">
        <PrimaryButton
          class="tw:!mt-3"
          :loading="isSubmitFlowPending || isPending"
          type="submit"
        >
          {{ t("models.auth.actions.resetPasswordConfirm") }}
        </PrimaryButton>
      </div>
    </v-form>
  </div>
</template>
